import React, { useEffect } from 'react'
import {
  LuArrowLeftRight,
  LuBuilding2,
  LuCheck,
  LuClock,
  LuFileText,
  LuShare2,
  LuUser,
} from 'react-icons/lu'

import ActionButton from '@/components/misc/ActionButton'
import LogReferralErrors from '@/components/modals/LogReferralModal/components/LogReferralErrors'
import QuestionSection from '@/components/modals/LogReferralModal/components/QuestionSection'
import ReferralClientDetails from '@/components/modals/LogReferralModal/components/ReferralClientDetails'
import SelectCompanyOrAdviser from '@/components/modals/LogReferralModal/components/SelectCompanyOrAdviser'
import SelectDirectionAndDate from '@/components/modals/LogReferralModal/components/SelectDirectionAndDate'
import SelectDirectReferralSource from '@/components/modals/LogReferralModal/components/SelectDirectReferralSource'
import SelectReferralSource from '@/components/modals/LogReferralModal/components/SelectReferralSource'
import SelectReferralUser from '@/components/modals/LogReferralModal/components/SelectReferralUser'
import SelectServiceArea from '@/components/modals/LogReferralModal/components/SelectServiceArea'
import SelectStatus from '@/components/modals/LogReferralModal/components/SelectStatus'
import SelectToCompanyUsers from '@/components/modals/LogReferralModal/components/SelectToCompanyUsers'
import SetCircumstances from '@/components/modals/LogReferralModal/components/SetCircumstances'
import { useLogHistoricalReferralMutation } from '@/components/modals/LogReferralModal/useLogHistoricalReferralMutation'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useInvalidateReferralsQueries } from '@/hooks/invalidateReferralsQueries'
import { useAddReferralsModal } from '@/hooks/useAddReferralsModal'
import useAppDispatch from '@/hooks/useAppDispatch'
import useAppSelector from '@/hooks/useAppSelector'
import { useCurrentUserOrFail } from '@/hooks/useCurrentUserOrFail'
import { useIsRoute } from '@/hooks/useIsRoute'
import useNotyf from '@/hooks/useNotyf'
import {
  DOM_IDS,
  IMPORT_FILE_NOTIFICATION_DURATION,
  REFERRAL_IMPORT_DIRECTION,
  REFERRALS_TAB,
} from '@/lib/constants'
import { ROUTES } from '@/lib/routes'
import errorTypeService from '@/lib/services/errorTypeService'
import { useHandleSuccessfulOutlookReferral } from '@/pages/officeTaskPane/components/OutlookReferral/hooks/useHandleSuccessfulOutlookReferral'
import {
  initializeState,
  resetLogReferralState,
  selectIsLoggingDirectLeadReferral,
  selectIsLoggingReceivedReferral,
  selectIsLoggingSentReferral,
  selectLogHistoricalReferralState,
} from '@/redux/slices/logHistoricalReferral'
import {
  selectCurrentCompanyOrFail,
  selectCurrentCompanyUser,
} from '@/redux/slices/session'

const LogReferralModal: React.FC = () => {
  const notyf = useNotyf()
  const currentCompany = useAppSelector(selectCurrentCompanyOrFail)
  const currentCompanyUser = useAppSelector(selectCurrentCompanyUser)
  const currentUser = useCurrentUserOrFail()
  const dispatch = useAppDispatch()
  const addReferralsModal = useAddReferralsModal()
  const logHistoricalReferralState = useAppSelector(
    selectLogHistoricalReferralState,
  )
  const invalidateReferralsQueries = useInvalidateReferralsQueries()
  const handleSuccessfulOutlookReferral = useHandleSuccessfulOutlookReferral()
  const isRoute = useIsRoute()
  const logHistoricalReferralMutation = useLogHistoricalReferralMutation()
  const isProcessing = logHistoricalReferralMutation.isPending
  const isLoggingDirectReferral = useAppSelector(
    selectIsLoggingDirectLeadReferral,
  )
  const isLoggingSentReferral = useAppSelector(selectIsLoggingSentReferral)
  const isLoggingReceivedReferral = useAppSelector(
    selectIsLoggingReceivedReferral,
  )

  const { isValid, logData, selectedUser } = logHistoricalReferralState

  useEffect(() => {
    if (currentCompanyUser?.id) {
      dispatch(
        initializeState({
          currentUser,
          currentCompanyUserId: currentCompanyUser.id,
          currentCompany: currentCompany,
        }),
      )
    }
  }, [dispatch, currentUser, currentCompanyUser, currentCompany])

  function onHide() {
    dispatch(resetLogReferralState())
  }

  async function handleSubmit() {
    if (!selectedUser?.company || !selectedUser?.user) {
      throw new Error('Expected selectedUser to be fully set')
    }

    await logHistoricalReferralMutation.mutateAsync(
      {
        logData,
        userCompany: selectedUser.company,
        userCompanyUser: selectedUser.user,
      },
      {
        onSuccess: async (referral) => {
          await invalidateReferralsQueries()

          if (isRoute(ROUTES._officeTaskPane)) {
            handleSuccessfulOutlookReferral(referral)
            return
          }

          notyf.success('Referral logged successfully')

          if (!isRoute(ROUTES.referrals())) {
            ROUTES.referrals({ searchParams: { tab: REFERRALS_TAB.sent } })
          }
        },
        onError: (error) => {
          if (errorTypeService.isConstraintViolationListError(error)) {
            setTimeout(() => {
              const errorBox = document.getElementById(
                DOM_IDS.logReferralErrorBox,
              )

              if (errorBox) {
                errorBox.scrollIntoView()
              }
            }, 200)
          }

          notyf.error({
            message: 'Failed to log the referral',
            duration: IMPORT_FILE_NOTIFICATION_DURATION,
          })
        },
      },
    )
  }

  return (
    <Dialog open={logHistoricalReferralState.isModalOpen} onHide={onHide}>
      <DialogContent className="bg-background p-6">
        <DialogHeader className="mb-2">
          <DialogTitle className="flex items-center gap-3">
            <div
              className="size-10 rounded-full bg-primary/10 flex items-center
                justify-center"
            >
              <LuClock className="text-primary text-xl" />
            </div>
            Log a referral
          </DialogTitle>
        </DialogHeader>

        <DialogBody className="max-w-full mx-0 px-0" scrollbar>
          {logHistoricalReferralState.logErrors && <LogReferralErrors />}

          <div className="flex flex-col gap-7 pb-4">
            <QuestionSection title="Client details" icon={<LuUser />}>
              <ReferralClientDetails disabled={isProcessing} />
            </QuestionSection>

            <QuestionSection
              title="Direction and date"
              icon={<LuArrowLeftRight />}
            >
              <SelectDirectionAndDate />
            </QuestionSection>

            {isLoggingReceivedReferral && (
              <QuestionSection
                title={
                  isLoggingSentReferral
                    ? 'How was this sent?'
                    : 'How was this received?'
                }
                icon={<LuShare2 />}
              >
                <SelectReferralSource disabled={isProcessing} />
              </QuestionSection>
            )}

            {isLoggingDirectReferral ? (
              <SelectDirectReferralSource />
            ) : (
              <QuestionSection
                title={
                  isLoggingSentReferral
                    ? 'Who did you refer to?'
                    : 'Who did you receive the referral from?'
                }
                icon={<LuBuilding2 />}
              >
                <SelectCompanyOrAdviser disabled={isProcessing} />
              </QuestionSection>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <QuestionSection title="Service" icon={<LuFileText />}>
                <SelectServiceArea disabled={isProcessing} />
              </QuestionSection>

              <QuestionSection title="Status" icon={<LuClock />}>
                <SelectStatus disabled={isProcessing} />
              </QuestionSection>
            </div>

            <QuestionSection
              title={
                <>
                  Circumstances{' '}
                  <span className="text-muted-foreground font-light">
                    (optional)
                  </span>
                </>
              }
            >
              <SetCircumstances disabled={isProcessing} />
            </QuestionSection>

            <SelectReferralUser disabled={isProcessing} />

            {isLoggingDirectReferral && (
              <SelectToCompanyUsers disabled={isProcessing} />
            )}
          </div>
        </DialogBody>

        <DialogFooter className="flex">
          <div
            className="w-full flex flex-col sm:flex-row justify-between gap-2"
          >
            <ActionButton
              disabled={!isValid || isProcessing}
              className="w-full sm:order-last sm:w-auto"
              isProcessing={isProcessing}
              variant="primary"
              size="sm"
              onClick={handleSubmit}
            >
              <LuCheck />
              {logHistoricalReferralState.logData.referralDirection ===
              REFERRAL_IMPORT_DIRECTION.TO
                ? 'Log sent referral'
                : 'Log received referral'}
            </ActionButton>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                onHide()
                addReferralsModal.showModal()
              }}
              className="w-full sm:w-auto text-[13px]"
            >
              Back
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default LogReferralModal
